
import { Component, Vue } from "vue-property-decorator";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import vTable from "@/components/vTable.vue";
import vImportCustomIngredientsModal from "@/components/ingredients/vImportCustomIngredientsModal.vue";
import vImportErrorsModal from "@/components/ingredients/vImportErrorsModal.vue";
import { mapState } from "vuex";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import ListRequest, { Ordering, Filter, OrderType } from "@/models/ListRequest";
import { createDefaultIngredient, Ingredient } from "@/models/Ingredient/Ingredient";
import RecipesRepository from "@/utils/API/RecipesRepository";
import IngredientsRepository from "@/utils/API/IngredientsRepository";
import AllergenRepository from "@/utils/API/AllergenRepository";
import { CreateRestaurantIngredient } from "@/models/Ingredient/CreateRestaurantIngredient";
import { UpdateRestaurantIngredient } from "@/models/Ingredient/UpdateRestaurantIngredient";
import vPreLoader from "@/components/vPreLoader.vue";
import { NutrientUnit } from "../../models/Ingredient/NutrientUnit";
import { NutrientUnitList } from "../../models/Ingredient/NutrientUnitList";
import { NutrientType } from "../../models/Ingredient/NutrientType";
import {round, getBoldString, desanitizeString, groupBy, downloadURI} from "@/utils/Helpers";
import { dateTimeDisplayFormat } from "@/utils/Defaults";
import vModal from "@/components/vModal.vue";
import { Snotify } from "vue-snotify";
import { ItemCompact } from "@/models/ItemCompact";
import { AddOnType } from "@/models/Subscriptions/AddOns/AddOnType";
import VAllergenStatusIcon from "@/components/allergens/VAllergenStatusIcon.vue";
import { getAllergenStatusMeta } from "@/utils/allergenStatus";
import vIconWithTooltip from "@/components/vIconWithTooltip.vue";
import vGenericErrorTable from "@/components/vGenericErrorTable.vue";
import { getErrorMessage } from "@/utils/Helpers";
import vUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";
import {AllergenConfigurationTargetType} from "@/models/Allergen/AllergenConfigurationData";

const vSelectFx = () => import("@/components/vSelectFx.vue");
const vIngredientManagement = () =>
  import("@/components/vIngredientManagement.vue");

export interface IngredientsState {
  count: number;
  totalCount: number;
  page: number;
  pageSize: number;
  filters: Filter[];
  orders: Ordering;
  loading: boolean;
  removeList: number[];
  ingredients: ItemCompact[];
  ingredientCompact : ItemCompact;
  ingredient: Ingredient;

  // not in use
  additionalNutrients: {
    unitHelper: NutrientUnitList | null;
    selectedUnit: NutrientUnit | null;
    value: number | null;
  }[];

  nutrients: NutrientUnitList[];
}

@Component({
  components: {
    VAllergenStatusIcon,
    vUpsellModal,
    vBreadcrumbs,
    vTable,
    vPreLoader,
    vModal,
    vSelectFx,
    vIngredientManagement,
    vIconWithTooltip,
    vGenericErrorTable,
    vImportCustomIngredientsModal,
    vImportErrorsModal
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
})
class Ingredients extends Vue {
  localizationState: any;
  accountState: any;
  desanitizeString = desanitizeString;
  ingredientsState: IngredientsState = {
    count: 0,
    totalCount: 0,
    page: 1,
    pageSize: 10,
    filters: [],
    orders: { field: "", orderType: OrderType.Asc },
    loading: true,
    removeList: [],
    ingredients: [],
    ingredient: createDefaultIngredient(),
    ingredientCompact: {
      id: 0,
      name: "",
      createdOn: undefined,
      updatedOn: undefined,
      createdUserName: "",
      updatedUserName: ""
    },
    nutrients: [],
    additionalNutrients: [],
  };

  // modals
  showModal = false;
  showDeletePrompt = false;
  deletePromptTitle = "";
  modalTitle = "";
  postSavePropagationReviewToken = 0;
  isAwaitingPostSavePropagationReview = false;
  pendingIngredientSaveSuccessMessage = "";
  checked: number[] = [];
  // import ingredients modal
  importingModal = {
    isOpen: false,
    file: "",
  };
  // import errors modal
  showImportErrorsModal = false;
  importIngredientsErrorsData: any[] = [];

  // user input on the search field
  searchString = "";

  editMode = false;

  // Dynamic table data
  tableHeaders: any[] = [];
  transformIngredient: any = {};

  // currently not in use as no additional nutrients are implemented
  showAll: boolean = false;

  recipesModal = {
    isOpen: false,
    customIngredient: 0,
    recipes: new Array(),
  };

  changeLogModal = {
    isOpen: false
  };

  showTrialUpsellModal: boolean = false;
  defaultTrialUpsellMessage = "This feature requires a paid subscription to use.";
  trialUpsellMessage = this.defaultTrialUpsellMessage;

  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
    this.trialUpsellMessage = this.defaultTrialUpsellMessage;
  }

  onIngredientPropagationReviewFinished() {
    if (!this.isAwaitingPostSavePropagationReview) {
      return;
    }

    this.isAwaitingPostSavePropagationReview = false;
    if (this.pendingIngredientSaveSuccessMessage) {
      this.$snotify.success(this.pendingIngredientSaveSuccessMessage);
      this.pendingIngredientSaveSuccessMessage = "";
    }
    this.resetState();
  }

  isDownloadButtonDisabled = false;

  /**
   * Handles the download of the ingredient export. If the user is on a trial account, it shows a trial upsell modal.
   * Otherwise, it initiates the download of the report file in Excel format.
   *
   * @return {Promise<void>} A promise that resolves when the report is successfully processed for download or the upsell modal is displayed.
   */
  async handleExportDownload() {
    if (this.accountState.userDetails.isTrial) {
      this.showTrialUpsellModal = true;
      return;
    }

    this.isDownloadButtonDisabled = true;
    await downloadURI(this.exportToExcelURL, "ingredients.xlsx");
    this.isDownloadButtonDisabled = false;
  }

  get tableHeaderData(){
    return [
      {
        name: this.translations.get("ingr_table_ene"),
        key: "e2",
        searchable: false,
        searchString: "",
        sortable: false,
        nutrientType : NutrientType.Energy,
      },
      {
        name: this.translations.get("ingr_table_prot") + " (g)",
        key: "e6",
        searchable: false,
        searchString: "",
        sortable: false,
        nutrientType : NutrientType.Protein,
      },
      {
        name: this.translations.get("ingr_table_carb") + " (g)",
        key: "e4",
        searchable: false,
        searchString: "",
        sortable: false,
        nutrientType : NutrientType.Carbohydrate,
      },
      {
        name: this.translations.get("ingr_table_fib") + " (g)",
        key: "e5",
        searchable: false,
        searchString: "",
        sortable: false,
        nutrientType : NutrientType.Fibre,
      },
      {
        name: this.translations.get("ingr_table_sod") + " (mg)",
        key: "e3",
        searchable: false,
        searchString: "",
        sortable: false,
        nutrientType : NutrientType.Sodium,
      },
    ];
  }
  /**
   * NYI
   */
  removeAdditionalNutrient(position: number) {
    this.ingredientsState.additionalNutrients.splice(position, 1);
  }

  /**
   * NYI
   */
  addAdditionalNutrient() {
    this.ingredientsState.additionalNutrients.push({
      unitHelper: null,
      selectedUnit: null,
      value: null,
    });
  }

  // Store import ingredients file
  storeFile() {
    // @ts-ignore
    this.importingModal.file = this.$refs.file.files[0];
  }

  // Close import ingredients modal and reset data
  closeImportIngredientsModal() {
    this.importingModal.file = null;
    this.importingModal.isOpen = false;
  }

  // Close import errors modal and reset data
  closeImportErrorsModal() {
    this.showImportErrorsModal = false;
    this.importIngredientsErrorsData = [];
  }

  get exportToExcelURL() {
    return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes/Ingredients/ExportIngredients`;
  }

  get downloadImportTemplate() {
    return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes/ingredients/ExportCustomIngredientsImportTemplate`;
  }

  get isRemoveDisabled() {
    return this.checked.length === 0;
  }

  get showControls() {
    return this.ingredientsState.totalCount > this.ingredientsState.count;
  }

  get isTrialUser() {
    return this.accountState.userDetails.isTrial;
  }

  get showAllergenAiButton() {
    return this.isTrialUser || this.isAllergenAiAddonActive;
  }

  get isAllergenAiAddonActive() {
    const addOns = this.accountState?.userDetails?.addOns || this.$store.getters.getUserDetails?.addOns || [];
    return addOns.some((addOn: any) => {
      const addOnType = typeof addOn === "object" ? addOn.addOn : addOn;
      return addOn?.isActive !== false && (addOnType === AddOnType.AllergenAI || addOnType === "AllergenAI");
    });
  }

  get showDensityWarningColumn() {
    return this.ingredientsState.ingredients.some((ingredient: ItemCompact) => ingredient.requiresDensityUpdate);
  }

  get ingredientsListColumnCount() {
    const baseColumnCount = this.isAllergenAiAddonActive ? 6 : 5;
    return this.showDensityWarningColumn ? baseColumnCount + 1 : baseColumnCount;
  }

  getAllergenStatusVariant(ingredient: ItemCompact) {
    return getAllergenStatusMeta(ingredient).variant;
  }

  getAllergenStatusTitle(ingredient: ItemCompact) {
    return getAllergenStatusMeta(ingredient).title;
  }

  getAllergenAiUpsellMessage() {
    const translation = this.translations.get("upsell_allergen_ai_message");
    return translation === "---"
      ? "'AI Allergen Detector' is available as a paid add-on. Upgrade your subscription to access the module."
      : translation;
  }

  goToSelectedIngredientAllergenAi() {
    if (this.isTrialUser) {
      this.trialUpsellMessage = this.getAllergenAiUpsellMessage();
      this.showTrialUpsellModal = true;
      return;
    }

    const ingredient = this.ingredientsState.ingredient;

    this.$router.push({
      name: "allergensAi",
      query: {
        tab: "customRecipes",
        id: String(ingredient.id),
        name: ingredient.name,
        analyzed: ingredient.hasAllergenAnalysis ? "1" : "0",
      },
    });
  }

  /**
   * Prepare the modal for deletion prompt and populate the list of ids for deletion
   */
  prepareDeletePrompt() {
    this.ingredientsState.removeList = this.checked;
    this.deletePromptTitle = this.translations.get(
      "ingr_delete_prompt_message",
      this.checked.length
    );
    this.showDeletePrompt = true;
  }

  get disabledNext() {
    return (
      Math.ceil(this.ingredientsState.totalCount / this.ingredientsState.pageSize) ===
      this.ingredientsState.page
    );
  }

  next() {
    if (!this.disabledNext) {
      this.ingredientsState.page = this.ingredientsState.page + 1;

      this.requestIngredients(
        new ListRequest({
          page: this.ingredientsState.page,
          pageSize: this.ingredientsState.pageSize,
          orders: [this.ingredientsState.orders],
        })
      );
    }
  }

  prev() {
    if (this.ingredientsState.page > 1) {
      this.ingredientsState.page = this.ingredientsState.page - 1;

      this.requestIngredients(
        new ListRequest({
          page: this.ingredientsState.page,
          pageSize: this.ingredientsState.pageSize,
          orders: [this.ingredientsState.orders],
        })
      );
    }
  }

  chooseIngredient(ingredientId: number) {
    IngredientsRepository.getIngredientDetails(ingredientId).then(async(response) => {
      this.ingredientsState.ingredient = { ...response.data.result };
      await this.loadAllergenConfiguration(
        AllergenConfigurationTargetType.Ingredient,
        ingredientId
      );
      this.populateTable();
    });
  }

  orderPageSize(value: any) {
    this.ingredientsState.pageSize = value;

    if (value === "0") {
      this.ingredientsState.pageSize = 10;
    }
    else if (value === "1") {
      this.ingredientsState.pageSize = 20;
    }
    else if (value === "2") {
      this.ingredientsState.pageSize = 50;
    }
    else if (value === "3") {
      this.ingredientsState.pageSize = 100;
    }

    this.requestIngredients(
      new ListRequest({
        page: this.ingredientsState.page,
        pageSize: this.ingredientsState.pageSize,
        filters: this.ingredientsState.filters,
        orders: [this.ingredientsState.orders],
      })
    );
  }

  /**
   * Method invoked after user confirms deletion in modal prompt
   */
  confirmDelete() {
    this.$snotify.async(
      this.translations.get(
        "ingr_toastr_deleting",
        this.ingredientsState.removeList.length
      ),
      () =>
        new Promise<Snotify>((resolve, reject) => {
          IngredientsRepository.deleteRestaurantIngredients({
            ids: this.ingredientsState.removeList,
          })
            .then(() => {
              resolve({
                body: this.translations.get(
                  "ingr_toastr_deleted",
                  this.ingredientsState.removeList.length
                ),
              });

              this.ingredientsState.page = 1;

              this.requestIngredients(
                new ListRequest({
                  page: this.ingredientsState.page,
                  pageSize: this.ingredientsState.pageSize,
                  filters: this.ingredientsState.filters,
                  orders: [this.ingredientsState.orders],
                })
              );

              this.ingredientsState.removeList = [];
              this.checked = [];
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
    this.resetState();
  }

  /**
   * Used by vTable after @change event is caught
   */
  changePage(args: any) {
    const orderings: Ordering[] = [];
    orderings.push({ ...args[3] });

    this.requestIngredients(
      new ListRequest({
        page: args[0],
        pageSize: this.ingredientsState.pageSize,
        filters: this.ingredientsState.filters,
        orders: orderings,
      })
    );
    this.ingredientsState.page = args[0];
    this.ingredientsState.orders = { ...args[3] };
  }

  order(filters: Filter[], ordering: Ordering) {
    const orderings: Ordering[] = [];
    orderings.push({ ...ordering });

    this.requestIngredients(
      new ListRequest({
        page: this.ingredientsState.page,
        pageSize: this.ingredientsState.pageSize,
        filters: this.ingredientsState.filters,
        orders: orderings,
      })
    );

    this.ingredientsState.orders = { ...ordering };
  }

  /**
   * NYI
   */
  showAllNutrients(flag: boolean) {
    this.showAll = flag;
    this.populateTable();
  }

  /**
   * Method for dynamically populating the vTable grid based on the headers from API
   */
  populateTable() {
    // Generate vTable friendly data for a single ingredient
    const ingredient = this.ingredientsState.ingredient;
    this.transformIngredient = [{
      ...this.generateIngredientData(ingredient)
    }];

    // Reset headers
    this.tableHeaders.length = 0;

    // Static headers
    let serving: any = {
      name: this.translations.get("ingr_table_serving"),
      key: "WeightInGrams",
      searchable: false,
      searchString: "",
      sortable: true,
    };

    // Add headers to tableHeaders
    this.tableHeaders = [serving, ...this.tableHeaderData];
  }

  // Helper function to generate data for a single ingredient
  generateIngredientData(ingredient: Ingredient): any {
    let result: any = {};
    const coreGroup = groupBy(Object.values(ingredient.nutrients), (x) => x.category).get("Core") || [];

    let count = 2;
    this.tableHeaderData.forEach((headerNutrient) => {
      let resultNutrient = coreGroup.find((i) => i.ingredientNutrient.nutrientUnit.nutrient.nutrientType == headerNutrient.nutrientType);
      if(resultNutrient){
        result[`e${count}`] = round(
          resultNutrient.ingredientNutrient.value *
            resultNutrient.ingredientNutrient.nutrientUnit.conversionCoeficient, 2);
      }
      else{
        result[`e${count}`] = 0;
      }
      count++;
    });

    result.e1 = parseFloat((Math.round(ingredient.weightInGrams * 100) / 100).toFixed(2));

    return {
      ...result,
      meta: {
        id: ingredient.id,
      },
    };
  }


  /**
   * Centralized place to request ingredients
   *
   * @param {ListRequest} listRequest
   */
  requestIngredients(listRequest: ListRequest) {
    this.ingredientsState.loading = true;

    IngredientsRepository.listRestaurantIngredients(listRequest)
      .then((response) => {
        this.ingredientsState.count = response.data.count;
        this.ingredientsState.totalCount =
          response.data.totalCount;
        this.ingredientsState.ingredients =
          response.data.result;
        this.ingredientsState.loading = false;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }
  /**
   * Format the headers to set the position of vitamin c next to vitamin d
   */
  formatTableHeadersForVitaminC(headers: any) {
    const indexC = headers.findIndex(
      (x: any) => x.nutrientType === NutrientType.VitaminC
    );
    const indexD = headers.findIndex(
      (x: any) => x.nutrientType === NutrientType.VitaminD
    );

    if (indexC != -1 && indexD != -1) {
      this.arraymove(headers, indexC, indexD + 1);
    }
  }
  /**
   * Format the headers to set the position of vitamin c next to vitamin d
   */
  formatTableDataForVitaminC(element: any) {
    const indexC = element.nutrients.findIndex(
      (x: any) =>
        x.nutrientUnit.nutrient.nutrientType === NutrientType.VitaminC
    );
    const indexD = element.nutrients.findIndex(
      (x: any) =>
        x.nutrientUnit.nutrient.nutrientType === NutrientType.VitaminD
    );
    if (indexC != -1 && indexD != -1) {
      this.arraymove(element.nutrients, indexC, indexD + 1);
    }

  }
  /**
   * Move element in array from one position to another
   */
  arraymove(arr: any, fromIndex: number, toIndex: number) {
    const element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
  }
  /**
   * Hides all modals and resets variables
   */
  resetState() {
    this.showModal = false;
    this.showDeletePrompt = false;
    this.isAwaitingPostSavePropagationReview = false;
    this.pendingIngredientSaveSuccessMessage = "";

    if( this.editMode){
      this.chooseIngredient(this.ingredientsState.ingredient.id);
    }

    this.editMode = false;
  }

  getFormatedDateTime(date: Date) {
    return this.$moment(date).format(dateTimeDisplayFormat);
  }

  /**
   * Prepare edit mode
   */
  edit(id: number) {
    this.editMode = true;
    this.modalTitle = this.translations.get("ingr_modal_edit_title");
    this.showModal = true;

    if (this.ingredientsState.ingredient.id !== id) {
      this.chooseIngredient(id);
    }
  }

  /**
   * Prepare add mode
   */
  add() {
    this.editMode = false;
    this.modalTitle = this.translations.get("ingr_modal_add_title");
    this.ingredientsState.ingredient = createDefaultIngredient();
    this.showModal = true;
  }

  /**
   * Submit handler, see submitEdit() and submitNew()
   */
  submit(
    _ingredient: Ingredient,
    nutrients: {
      value: number;
      nutrientUnitId: number;
    }[],
    _selectedAllergens?: any,
    _mayContainText?: any,
    options?: { openAiAfterSave?: boolean }
  ) {
    if (this.editMode) {
      this.submitEdit(nutrients, options);
    }
    else {
      this.submitNew(nutrients, options);
    }
  }

  submitEdit(
    nutrients: {
      value: number;
      nutrientUnitId: number;
    }[],
    options?: { openAiAfterSave?: boolean }
  ) {
    // update ingredien model
    let updateIngredient: UpdateRestaurantIngredient = {
      measurements: this.ingredientsState.ingredient.allMeasurements,
      id: this.ingredientsState.ingredient.id,
      name: this.ingredientsState.ingredient.name,
      weightInGrams: this.ingredientsState.ingredient.weightInGrams,
      unitOfMeasurement: this.ingredientsState.ingredient.unitOfMeasurement,
      categoryDensityId: this.ingredientsState.ingredient.categoryDensityId,
      quantityInUnitOfMeasurement:
        this.ingredientsState.ingredient.quantityInUnitOfMeasurement,
      // the model from above
      nutrients: nutrients,
      subIngredients: this.ingredientsState.ingredient.subIngredients,
      foodCode: this.ingredientsState.ingredient.foodCode,
      allergens: this.ingredientsState.ingredient.allergens || [],
      mayContainText: this.ingredientsState.ingredient.mayContainText || {},
      //supplierName: this.ingredientsState.ingredient.supplierName
    };

    // actual submit of the model
    this.$snotify.async(
      this.translations.get("ingr_toastr_updating"),
      () =>
        new Promise<Snotify>((resolve, reject) => {
          IngredientsRepository.updateRestaurantIngredient(updateIngredient)
            .then(async() => {
              await this.saveAllergenConfiguration(
                AllergenConfigurationTargetType.Ingredient,
                updateIngredient.id
              );

              if (options?.openAiAfterSave) {
                await this.loadAllergenConfiguration(
                  AllergenConfigurationTargetType.Ingredient,
                  updateIngredient.id
                );

                this.editMode = true;
                this.modalTitle = this.translations.get("ingr_modal_edit_title");

                resolve({
                  body: "Custom ingredient saved successfully. Running allergen analysis...",
                });

                this.requestIngredients(
                  new ListRequest({
                    page: this.ingredientsState.page,
                    pageSize: this.ingredientsState.pageSize,
                    filters: this.ingredientsState.filters,
                    orders: [this.ingredientsState.orders],
                  })
                );

                return;
              }

              this.pendingIngredientSaveSuccessMessage = this.translations.get(
                "ingr_toastr_updated",
                updateIngredient.name
              );

              resolve({
                body: "Checking which recipes are affected by these allergen changes...",
              });

              this.requestIngredients(
                new ListRequest({
                  page: this.ingredientsState.page,
                  pageSize: this.ingredientsState.pageSize,
                  filters: this.ingredientsState.filters,
                  orders: [this.ingredientsState.orders],
                })
              );

              this.isAwaitingPostSavePropagationReview = true;
              this.postSavePropagationReviewToken += 1;
            })
            .catch((error) => {
              let message = "";
              this.edit(this.ingredientsState.ingredient.id);
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
  }

  submitNew(
    nutrients: {
      value: number;
      nutrientUnitId: number;
    }[],
    options?: { openAiAfterSave?: boolean }
  ) {
    // ingredient creation model
    let newIngredient: CreateRestaurantIngredient = {
      name: this.ingredientsState.ingredient.name,
      weightInGrams: this.ingredientsState.ingredient.weightInGrams,
      measurements: this.ingredientsState.ingredient.allMeasurements,
      unitOfMeasurement: this.ingredientsState.ingredient.unitOfMeasurement,
      categoryDensityId: this.ingredientsState.ingredient.categoryDensityId,
      quantityInUnitOfMeasurement:
        this.ingredientsState.ingredient.quantityInUnitOfMeasurement,
      // the model from above
      nutrients: nutrients,
      subIngredients: this.ingredientsState.ingredient.subIngredients,
      foodCode: this.ingredientsState.ingredient.foodCode,
      allergens: this.ingredientsState.ingredient.allergens || [],
      mayContainText: this.ingredientsState.ingredient.mayContainText || {},
      //supplierName: this.ingredientsState.ingredient.supplierName,
    };

    // the request
    this.$snotify.async(
      this.translations.get("ingr_toastr_adding"),
      () =>
        new Promise<Snotify>((resolve, reject) => {
          IngredientsRepository.createRestaurantIngredient(newIngredient)
            .then(async(response) => {
              const ingredientId = this.getCreatedEntityId(response);

              if (ingredientId !== null) {
                await this.saveAllergenConfiguration(
                  AllergenConfigurationTargetType.Ingredient,
                  ingredientId
                );
              }

              if (options?.openAiAfterSave && ingredientId !== null) {
                const ingredientResponse = await IngredientsRepository.getIngredientDetails(ingredientId);

                this.ingredientsState.ingredient = { ...ingredientResponse.data.result };
                await this.loadAllergenConfiguration(
                  AllergenConfigurationTargetType.Ingredient,
                  ingredientId
                );

                this.editMode = true;
                this.modalTitle = this.translations.get("ingr_modal_edit_title");

                resolve({
                  body: "Custom ingredient saved successfully. Running allergen analysis...",
                });

                this.requestIngredients(
                  new ListRequest({
                    page: this.ingredientsState.page,
                    pageSize: this.ingredientsState.pageSize,
                    filters: this.ingredientsState.filters,
                    orders: [this.ingredientsState.orders],
                  })
                );

                return;
              }

              this.pendingIngredientSaveSuccessMessage = this.translations.get(
                "ingr_toastr_added",
                newIngredient.name
              );

              resolve({
                body: "Checking which recipes are affected by these allergen changes...",
              });

              this.requestIngredients(
                new ListRequest({
                  page: this.ingredientsState.page,
                  pageSize: this.ingredientsState.pageSize,
                  filters: this.ingredientsState.filters,
                  orders: [this.ingredientsState.orders],
                })
              );

              this.isAwaitingPostSavePropagationReview = true;
              this.postSavePropagationReviewToken += 1;
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
  }

  async saveAllergenConfiguration(targetType: AllergenConfigurationTargetType, targetId: number) {
    await AllergenRepository.saveAllergenConfiguration({
      targetType,
      targetId,
      allergens: this.ingredientsState.ingredient.allergens || [],
      mayContainText: this.ingredientsState.ingredient.mayContainText || {}
    });
  }

  async saveIngredientAllergenAliases(ingredientId: number) {
    if (!ingredientId) {
      return;
    }

    await this.saveAllergenConfiguration(
      AllergenConfigurationTargetType.Ingredient,
      ingredientId
    );
  }

  async loadAllergenConfiguration(targetType: AllergenConfigurationTargetType, targetId: number) {
    try {
      const response = await AllergenRepository.getAllergenConfiguration(targetType, targetId);

      this.ingredientsState.ingredient.allergens = response.data?.allergens || [];
      this.ingredientsState.ingredient.mayContainText = response.data?.mayContainText || {};
    }
    catch {
      this.ingredientsState.ingredient.allergens = [];
      this.ingredientsState.ingredient.mayContainText = {};
    }
  }

  getCreatedEntityId(response: any): number | null {
    return response?.data?.id
      || response?.data?.result?.id
      || response?.data?.result
      || null;
  }

  //Get all recipes that are using chosen custom ingredient
  getAllRecipesThatUseIngredient() {
    RecipesRepository.listRecipesThatUseIngredient(
      this.recipesModal.customIngredient
    )
      .then((response) => {
        this.recipesModal.recipes = response.data.result;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  onRecipeClick(id: number) {
    this.recipesModal.customIngredient = id;
    this.recipesModal.isOpen = true;
    this.getAllRecipesThatUseIngredient();
  }

  onChangeLogClick(id: number){
    this.changeLogModal.isOpen = true;
    this.ingredientsState.ingredientCompact =  this.ingredientsState.ingredients.find((obj) => obj.id === id);
  }

  get translations() {
    return this.localizationState.translations;
  }

  get isLocalOrQA(){
    const env = process.env.VUE_APP_SHOW_EXPERIMENTAL;
    return env === 'true';
  }

  created() {
    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: ["common", "recipes", "ingredients", "nutrition-labels"],
      })
      .then(() => {
        document.title =
          this.translations.get("ingr_title") +
          " " +
          this.translations.get("meta_title_postfix");
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });

    this.requestIngredients(
      new ListRequest({ page: 1, pageSize: this.ingredientsState.pageSize })
    );
  }

  mounted() {
    (<any>window).$.Pages.init();
    this.initSearch();
  }

  initSearch(){
    // destroy previous select2 instance if needed
    if ((<any>$("#select2Ingredients")).hasClass("select2-hidden-accessible")) {
      (<any>$("#select2Ingredients")).html("").select2();
    };

    let searchTerm = "";

    const restoreSearchTerm = () => {
      this.$nextTick(() => {
        const select2 = $("#select2Ingredients").data("select2");
        if (!select2 || !select2.dropdown || !select2.dropdown.$search) return;

        const $search = select2.dropdown.$search;

        $search
          .val(searchTerm)
          .trigger("input")
          .off("input.fullreset")
          .on("input.fullreset", () => {
            searchTerm = $search.val();

            // If cleared, reset to full list
            if (!searchTerm) {
              this.resetIngredientFilters();
            }
          });
      });
    };

    (<any>$("#select2Ingredients"))
      .select2({
        allowClear: false,
        width: "100%",
        placeholder: "Search Ingredients",
        minimumInputLength: 3,
        ajax: {
          delay: 500,
          transport: (params: any, success: any) => {
            IngredientsRepository.listIngredientSearch(
              new ListRequest({
                page: this.ingredientsState.page,
                pageSize: this.ingredientsState.pageSize,
                searchTerm: params.data.term ? params.data.term : "",
                orders: [this.ingredientsState.orders],
              })
            ).then((response) => {
              success(response);
            });
            searchTerm = params.data.term;
          },
          processResults: (response: any) => {
            let data: any[] = [];
            data.push({ id: 0, text: getBoldString("All Custom Ingredients", "") });
            response.data.result.ingredientList.result.map((r: any) => {
              data.push({ id: r.id, text: getBoldString(r.name, searchTerm) });
            });

            return { results: data };
          },
        },
        escapeMarkup: function(element:string){
          return element;
        },
      })
      .on("select2:select", (eventData: any) => {
        this.ingredientsState.page = 1;
        this.ingredientsState.orders = { field: "", orderType: OrderType.Asc };

        if (eventData.target.value == 0) {
          this.ingredientsState.filters.length = 0;
        }
        else {
          this.ingredientsState.filters = [
            { name: "Id", value: eventData.target.value },
          ];
        }
        IngredientsRepository.listRestaurantIngredients(
          new ListRequest({
            page: this.ingredientsState.page,
            pageSize: this.ingredientsState.pageSize,
            filters: this.ingredientsState.filters,
            orders: [this.ingredientsState.orders],
          })
        ).then((response) => {
          this.ingredientsState.ingredients =
            response.data.result;
          this.ingredientsState.count = response.data.count;
          this.ingredientsState.totalCount =
            response.data.totalCount;
          this.ingredientsState.loading = false;

        });
      })
      .on("select2:open", () => {
        restoreSearchTerm();
      });
  }

  resetIngredientFilters() {
    this.ingredientsState.page = 1;
    this.ingredientsState.filters = [];

    IngredientsRepository.listRestaurantIngredients(
      new ListRequest({
        page: this.ingredientsState.page,
        pageSize: this.ingredientsState.pageSize,
        orders: [this.ingredientsState.orders],
      })
    ).then((response) => {
      this.ingredientsState.ingredients = response.data.result;
      this.ingredientsState.count = response.data.count;
      this.ingredientsState.totalCount = response.data.totalCount;
      this.ingredientsState.loading = false;
    });
  }

  async importCustomIngredients() {
    const formData = new FormData();
    formData.append("file", this.importingModal.file);

    // @ts-ignore
    this.$snotify.async(
      this.translations.get("ingr_loading"),
      async() => {
        try {
          await IngredientsRepository.uploadCustomIngredients(formData);

          this.closeImportIngredientsModal();

          await this.requestIngredients(
            new ListRequest({
              page: 1,
              pageSize: this.ingredientsState.pageSize
            })
          );

          return {
            body: this.translations.get("ms_save_success")
          };
        }
        catch (error: any) {
          this.closeImportIngredientsModal();

          if (error.response.data.result &&
            error.response.data.result.length > 0) {
            this.showImportErrorsModal = true;
            this.importIngredientsErrorsData = error.response.data.result;
          }

          throw {
            body: getErrorMessage(error, this.translations)
          };
        }
      },
      {
        timeout: 3500,
        closeOnClick: true
      }
    );
  }

}
export default Ingredients;
